import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import * as PIXI from "pixi.js";
import { Spine } from "@esotericsoftware/spine-pixi-v7";
import "./index.css";

type LoadingSpineProps = {
  fullscreen?: boolean;
};

type LayoutConfig = {
  scale: number;
  offsetX: number;
  offsetY: number;
};

const LOADER_ASSET_BASE = "/loadingSpine/MEEMPLAYextramini/MEEMPLAY";

let loaderInstanceId = 0;

const getLayoutConfig = (width: number, height: number): LayoutConfig => {
  if (width <= 480) return { scale: 0.6, offsetX: 0, offsetY: 0 };

  if (width <= 768) return { scale: 1, offsetX: 0, offsetY: 0 };

  if (width <= 1024) {
    return { scale: 1, offsetX: 0, offsetY: 0 };
  }

  return { scale: 1.75, offsetX: 0, offsetY: 0 };
};

export default function LoadingSpine({ fullscreen = true }: LoadingSpineProps) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const assetAliasBaseRef = useRef(`loading-spine-${loaderInstanceId++}`);

  useEffect(() => {
    const host = hostRef.current;

    if (!host) return;

    let isCancelled = false;
    let app: PIXI.Application | undefined;
    let spine: Spine | undefined;
    let resizeObserver: ResizeObserver | undefined;

    const applyRendererDpr = () => {
      if (!app) return;

      const dpr = Math.max(1, window.devicePixelRatio || 1);
      const width = Math.max(1, Math.floor(host.clientWidth));
      const height = Math.max(1, Math.floor(host.clientHeight));

      app.renderer.resolution = dpr;
      app.renderer.resize(width, height);
    };

    const applyLayout = () => {
      if (!app || !spine) return;

      const { scale, offsetX, offsetY } = getLayoutConfig(
        host.clientWidth,
        host.clientHeight,
      );

      spine.scale.set(scale);
      spine.pivot.set(0, 0);
      spine.position.set(0, 0);

      const bounds = spine.getLocalBounds();

      spine.pivot.set(
        bounds.x + bounds.width / 2,
        bounds.y + bounds.height / 2,
      );
      spine.position.set(
        app.screen.width / 2 + offsetX,
        app.screen.height / 2 + offsetY,
      );
    };

    const syncLayout = () => {
      applyRendererDpr();
      applyLayout();
    };

    const initialize = async () => {
      const nextApp = new PIXI.Application({
        resizeTo: host,
        backgroundAlpha: 0,
        antialias: true,
        resolution: Math.max(1, window.devicePixelRatio || 1),
        autoDensity: true,
      });

      if (isCancelled) {
        nextApp.destroy(true);
        return;
      }

      app = nextApp;
      const canvas = app.view as HTMLCanvasElement;
      canvas.style.width = "100%";
      canvas.style.height = "100%";
      canvas.style.display = "block";
      host.appendChild(canvas);

      const atlasKey = `${assetAliasBaseRef.current}-atlas`;
      const skeletonKey = `${assetAliasBaseRef.current}-skeleton`;

      PIXI.Assets.add({ alias: atlasKey, src: `${LOADER_ASSET_BASE}.atlas` });
      PIXI.Assets.add({ alias: skeletonKey, src: `${LOADER_ASSET_BASE}.json` });

      await PIXI.Assets.load([atlasKey, skeletonKey]);

      if (isCancelled || !app) {
        nextApp.destroy(true);
        return;
      }

      spine = Spine.from({ skeleton: skeletonKey, atlas: atlasKey });
      app.stage.addChild(spine);

      const firstAnimation = spine.skeleton.data.animations?.[0]?.name;
      if (firstAnimation) {
        spine.state.setAnimation(0, firstAnimation, true);
      }

      syncLayout();

      window.addEventListener("resize", syncLayout, { passive: true });
      if (typeof ResizeObserver !== "undefined") {
        resizeObserver = new ResizeObserver(syncLayout);
        resizeObserver.observe(host);
      }
    };

    initialize().catch(() => undefined);

    return () => {
      isCancelled = true;
      window.removeEventListener("resize", syncLayout);
      resizeObserver?.disconnect();
      spine?.destroy({ children: true });
      app?.destroy(true);
    };
  }, []);

  const overlay = (
    <div
      className={`loading-spine-overlay ${
        fullscreen
          ? "loading-spine-overlay--fullscreen"
          : "loading-spine-overlay--contained"
      }`}
    >
      <div className="loading-spine-shell">
        <div ref={hostRef} className="loading-spine-host" />
      </div>
    </div>
  );

  if (fullscreen && typeof document !== "undefined") {
    return createPortal(overlay, document.body);
  }

  return overlay;
}
